import React from "react";
import { formatCurrency } from "../cartPaymentLinkConstants";

/**
 * Wallet-rules detail panel shown behind the info icon on the cart payment
 * method screen. Driven by the preview `sublimit` block (same shape as the
 * appointment sublimit): available balance, eligible wallet amount, and the
 * applicable category/service sub-limit, per-transaction and co-pay rules.
 */
const CartWalletRulesInfoBox = ({ transactionData }) => {
  const isWalletUnlimited = transactionData?.isUnlimitedWallet ?? false;
  const category = transactionData?.category;
  const service = transactionData?.service;

  const showCategorySublimit =
    category?.applicable === true && category?.sublimit?.enabled === true;

  const showPerTxnLimit =
    category?.applicable === true &&
    category?.perTransactionLimit?.enabled === true;

  const showCopayLimit =
    category?.applicable === true && category?.copayLimit?.enabled === true;

  const showServiceLimit = service?.applicable === true;

  const showFinalNote =
    [
      showCategorySublimit,
      showPerTxnLimit,
      showCopayLimit,
      showServiceLimit,
    ].filter(Boolean).length > 1;

  const formatValueBasedOnUnit = (value, unit) => {
    if (value == null || unit == null) return "-";
    const parsed = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(parsed)) return "-";

    if (unit === 1) {
      const hasDecimal = parsed % 1 !== 0;
      return hasDecimal ? `${parsed.toFixed(2)}%` : `${parsed.toFixed(0)}%`;
    } else if (unit === 2) {
      return formatCurrency(parsed);
    }
    return "-";
  };

  return (
    <div className="wallet-rules-box zy-dashed-border">
      {!isWalletUnlimited && (
        <div className="rule-row">
          <span className="bold600">
            Available Wallet Balance:{" "}
            {formatCurrency(transactionData?.zylaWallet?.available)}
          </span>
        </div>
      )}

      <div className="rule-row">
        <span className="bold600">
          Eligible Wallet Amount:{" "}
          {formatCurrency(transactionData?.useZylaWallet)}
        </span>
      </div>

      <div className="rules-container">
        {showServiceLimit && (
          <div className="rule-row">
            Available sub-limit {service?.serviceCardName || ""}:{" "}
            {formatCurrency(service?.sublimitBalance?.available)} out of{" "}
            {formatCurrency(service?.sublimitBalance?.total)}
          </div>
        )}

        {showServiceLimit && showCategorySublimit && (
          <div className="rule-row">
            Available sub-limit prescribed diagnostics:{" "}
            {formatCurrency(category?.sublimitBalance?.available)} out of{" "}
            {formatCurrency(category?.sublimitBalance?.total)}
          </div>
        )}

        {!showServiceLimit && showCategorySublimit && (
          <div className="rule-row">
            Available sub-limit:{" "}
            {formatCurrency(category?.sublimitBalance?.available)} out of{" "}
            {formatCurrency(category?.sublimitBalance?.total)}
          </div>
        )}

        {showPerTxnLimit && (
          <div className="rule-row">
            Per transaction limit:{" "}
            {formatValueBasedOnUnit(
              category?.perTransactionLimit?.value,
              category?.perTransactionLimit?.unit,
            )}
          </div>
        )}

        {showCopayLimit && (
          <div className="rule-row">
            Self Co-pay limit:{" "}
            {formatValueBasedOnUnit(
              category?.copayLimit?.value,
              category?.copayLimit?.unit,
            )}
          </div>
        )}

        {showFinalNote && (
          <div className="rule-row note">
            Eligible wallet amount is minimum of the wallet rules mentioned
            above
          </div>
        )}
      </div>
    </div>
  );
};

export default CartWalletRulesInfoBox;
